import React, { useState, useEffect } from 'react';
import Dashboard from './components/Dashboard';
import Events from './components/Events';
import Portfolio from './components/Portfolio';
import './App.css';
import { ThemeProvider, createTheme } from '@mui/material';
import { CssBaseline } from '@mui/material';
import { Typography, Box, IconButton, Tooltip, Link } from '@mui/material';
import TwitterIcon from '@mui/icons-material/Twitter';
import TelegramIcon from '@mui/icons-material/Telegram';
import { motion } from 'framer-motion';
import TagIcon from '@mui/icons-material/Tag'; // For Discord

// Define seasons outside the component
const GAME_SEASONS = {
  TRUMP_SEASON: {
    name: "Trump's Meme Era",
    coins: [
      { id: 'trump1', name: '🎺 TrumpCoin', price: 0.04, trend: '📈', volatility: 0.5 },
      { id: 'trump2', name: '🗽 FreedomToken', price: 0.02, trend: '🚀', volatility: 0.6 },
      { id: 'trump3', name: '🦅 PatriotCoin', price: 0.03, trend: '📈', volatility: 0.45 },
    ],
    duration: 120000, // 2 minutes for testing, adjust as needed
    events: [
      "🎤 Trump announces his own crypto platform!",
      "🏛️ SEC investigating meme tokens",
      "🐦 Truth Social adds crypto trading"
    ]
  },
  AI_SEASON: {
    name: "AI Token Mania",
    coins: [
      { id: 'ai1', name: '🤖 AIToken', price: 0.06, trend: '📈', volatility: 0.4 },
      { id: 'ai2', name: '🧠 NeuralCoin', price: 0.03, trend: '🚀', volatility: 0.55 },
      { id: 'ai3', name: '💡 SmartToken', price: 0.05, trend: '📈', volatility: 0.5 },
    ],
    duration: 120000,
    events: [
      "🤖 ChatGPT predicts crypto boom!",
      "🧠 AI trading bots gone wild",
      "💻 New AI breakthrough pumps market"
    ]
  }
};

function App() {
  const [wallet, setWallet] = useState(1000);
  const [coins, setCoins] = useState([
    { id: 1, name: '🐕 DogeKing', price: 0.05, trend: '🚀', volatility: 0.3 },
    { id: 2, name: '🍌 BananaBonk', price: 0.01, trend: '📉', volatility: 0.5 },
    { id: 3, name: '😺 CatCoin', price: 0.03, trend: '📈', volatility: 0.2 },
    { id: 4, name: '🌙 RocketMoon', price: 0.07, trend: '🚀', volatility: 0.6 },
    { id: 5, name: '🦍 ApeToken', price: 0.04, trend: '📈', volatility: 0.4 },
    { id: 6, name: '💎 DiamondPaw', price: 0.02, trend: '📉', volatility: 0.45 },
  ]);
  const [events, setEvents] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [darkMode, setDarkMode] = useState(false);
  const [currentSeason, setCurrentSeason] = useState(null);
  const [seasonalCoins, setSeasonalCoins] = useState([]);
  
  const theme = createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
    },
  });

  // Enhanced news and market events
  const newsEvents = [
    {
      type: 'ELON_TWEET',
      message: (coinName) => `🐦 ELON ALERT: Elon Musk just tweeted "${coinName} to Mars! 🚀"`,
      effect: (price) => price * (1.5 + Math.random()),
      probability: 0.2
    },
    {
      type: 'FLASH_CRASH',
      message: (coinName) => `💥 FLASH CRASH: ${coinName} whales dumping! Panic selling everywhere!`,
      effect: (price) => price * (0.4 + Math.random() * 0.3),
      probability: 0.1
    },
    {
      type: 'CELEBRITY',
      message: (coinName) => `🌟 CELEBRITY SHILL: Kim Kardashian promotes ${coinName}!`,
      effect: (price) => price * (1.3 + Math.random() * 0.7),
      probability: 0.15
    },
    {
      type: 'EXCHANGE',
      message: (coinName) => `📈 BINANCE LISTING: ${coinName} getting listed in 10 minutes!`,
      effect: (price) => price * (2 + Math.random()),
      probability: 0.1
    },
    {
      type: 'FUD',
      message: (coinName) => `⚠️ MAJOR FUD: ${coinName} smart contract vulnerability found!`,
      effect: (price) => price * (0.6 + Math.random() * 0.2),
      probability: 0.15
    }
  ];

  // Handle buying coins
  const handleBuy = (coin, amount) => {
    const cost = coin.price * amount;
    if (wallet >= cost) {
      setWallet(prev => prev - cost);
      setPortfolio(prev => {
        const existingPosition = prev.find(p => p.coinId === coin.id);
        if (existingPosition) {
          return prev.map(p => 
            p.coinId === coin.id 
              ? { ...p, amount: p.amount + amount, avgPrice: (p.avgPrice * p.amount + cost) / (p.amount + amount) }
              : p
          );
        }
        return [...prev, { coinId: coin.id, name: coin.name, amount, avgPrice: coin.price }];
      });
      checkAchievements();
    }
  };

  // Handle selling coins
  const handleSell = (coin, amount) => {
    const position = portfolio.find(p => p.coinId === coin.id);
    if (position && position.amount >= amount) {
      const revenue = coin.price * amount;
      setWallet(prev => prev + revenue);
      setPortfolio(prev => {
        const updatedPortfolio = prev.map(p => 
          p.coinId === coin.id 
            ? { ...p, amount: p.amount - amount }
            : p
        ).filter(p => p.amount > 0);
        return updatedPortfolio;
      });
      checkAchievements();
    }
  };

  // Check and award achievements
  const checkAchievements = () => {
    const newAchievements = [];
    
    // First trade achievement
    if (portfolio.length === 1 && !achievements.includes('First Trade')) {
      newAchievements.push('First Trade');
    }
    
    // Portfolio diversity achievement
    if (portfolio.length >= 3 && !achievements.includes('Diversified')) {
      newAchievements.push('Diversified');
    }
    
    // Whale achievement
    const totalValue = portfolio.reduce((sum, pos) => {
      const coin = coins.find(c => c.id === pos.coinId);
      return sum + (pos.amount * coin.price);
    }, 0);
    
    if (totalValue > 5000 && !achievements.includes('Whale Status')) {
      newAchievements.push('Whale Status');
    }

    if (newAchievements.length > 0) {
      setAchievements(prev => [...prev, ...newAchievements]);
    }
  };

  // Enhanced market events trigger
  const triggerEvent = () => {
    // Combine regular and seasonal events
    const allCoins = [...coins, ...seasonalCoins];
    const targetCoin = allCoins[Math.floor(Math.random() * allCoins.length)];
    
    // Random event selection with probability weights
    const eventPool = [...newsEvents];
    if (currentSeason) {
      eventPool.push(...GAME_SEASONS[currentSeason].events.map(evt => ({
        type: 'SEASONAL',
        message: () => evt,
        effect: (price) => price * (0.7 + Math.random() * 1.3),
        probability: 0.2
      })));
    }

    // Select event based on probability
    const random = Math.random();
    let probabilitySum = 0;
    const selectedEvent = eventPool.find(evt => {
      probabilitySum += evt.probability;
      return random <= probabilitySum;
    }) || eventPool[0];

    const eventMessage = selectedEvent.message(targetCoin.name);
    setEvents(prev => [eventMessage, ...prev].slice(0, 10)); // Keep last 10 events

    // Update coin prices
    const updateCoins = (coinList) => 
      coinList.map(coin => 
        coin.id === targetCoin.id 
          ? { ...coin, price: selectedEvent.effect(coin.price) }
          : coin
      );

    setCoins(prev => updateCoins(prev));
    if (seasonalCoins.length > 0) {
      setSeasonalCoins(prev => updateCoins(prev));
    }
  };

  // Season management
  useEffect(() => {
    const seasonKeys = Object.keys(GAME_SEASONS);
    const startNewSeason = () => {
      const randomSeason = seasonKeys[Math.floor(Math.random() * seasonKeys.length)];
      setCurrentSeason(randomSeason);
      setSeasonalCoins(GAME_SEASONS[randomSeason].coins);
      setEvents(prev => [
        `🎉 NEW SEASON STARTED: ${GAME_SEASONS[randomSeason].name}!`,
        ...prev
      ]);

      // End season after duration
      setTimeout(() => {
        setCurrentSeason(null);
        setSeasonalCoins([]);
        setEvents(prev => [`🏁 ${GAME_SEASONS[randomSeason].name} has ended!`, ...prev]);
      }, GAME_SEASONS[randomSeason].duration);
    };

    // Start first season after delay
    const seasonTimer = setTimeout(startNewSeason, 30000);
    return () => clearTimeout(seasonTimer);
  }, []);

  // Enhanced market simulation
  useEffect(() => {
    const interval = setInterval(() => {
      // Update regular coins
      setCoins(prevCoins => prevCoins.map(coin => {
        const change = 1 + (Math.random() - 0.5) * (coin.volatility * 1.5);
        return {
          ...coin,
          price: coin.price * change,
          trend: change > 1 ? '📈' : '📉',
        };
      }));

      // Update seasonal coins
      if (seasonalCoins.length > 0) {
        setSeasonalCoins(prevCoins => prevCoins.map(coin => {
          const change = 1 + (Math.random() - 0.5) * (coin.volatility * 2); // More volatile
          return {
            ...coin,
            price: coin.price * change,
            trend: change > 1 ? '📈' : '📉',
          };
        }));
      }

      // Trigger events with increased complexity
      if (Math.random() < 0.3) {
        triggerEvent();
      }
    }, 4000);
    
    return () => clearInterval(interval);
  }, [seasonalCoins]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <div className="App">
        <Typography variant="h3" component="h1" sx={{ 
          fontWeight: 700,
          mb: 2,
          background: darkMode ? 
            'linear-gradient(45deg, #FFD700 30%, #FFA500 90%)' : 
            'linear-gradient(45deg, #FFD700 30%, #FF6B6B 90%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          textShadow: '0 0 20px rgba(255, 215, 0, 0.3)'
        }}>
          Moon Jungle 🌙🌴
        </Typography>
        {currentSeason && (
          <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
            ⚔️ Current Battle: {GAME_SEASONS[currentSeason].name}
          </Typography>
        )}
        <Dashboard 
          coins={[...coins, ...seasonalCoins]} 
          wallet={wallet} 
          onBuy={handleBuy} 
          onSell={handleSell}
          portfolio={portfolio}
          darkMode={darkMode}
          setDarkMode={setDarkMode}
        />
        <Events events={events} />
        <Portfolio 
          wallet={wallet} 
          portfolio={portfolio} 
          coins={[...coins, ...seasonalCoins]}
          achievements={achievements}
        />

        {/* Social Links Footer */}
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            padding: 2,
            display: 'flex',
            justifyContent: 'center',
            gap: 2,
            background: darkMode ? 
              'linear-gradient(to top, rgba(0,0,0,0.9), rgba(0,0,0,0))' : 
              'linear-gradient(to top, rgba(255,255,255,0.9), rgba(255,255,255,0))',
            backdropFilter: 'blur(5px)',
          }}
        >
          <Tooltip title="Follow us on Twitter" arrow>
            <IconButton
              component={motion.a}
              href="https://twitter.com/apeoutmeme"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              sx={{
                background: 'linear-gradient(45deg, #1DA1F2 30%, #1a91da 90%)',
                color: 'white',
                '&:hover': {
                  background: 'linear-gradient(45deg, #1a91da 30%, #1778b5 90%)',
                }
              }}
            >
              <TwitterIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Join our Discord" arrow>
            <IconButton
              component={motion.a}
              href="https://discord.com/invite/rPfqzCGr"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              sx={{
                background: 'linear-gradient(45deg, #7289DA 30%, #5865F2 90%)',
                color: 'white',
                '&:hover': {
                  background: 'linear-gradient(45deg, #5865F2 30%, #454FBF 90%)',
                }
              }}
            >
              <TagIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Join our Telegram" arrow>
            <IconButton
              component={motion.a}
              href="https://t.me/apeoutmeme"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              sx={{
                background: 'linear-gradient(45deg, #0088cc 30%, #0077b5 90%)',
                color: 'white',
                '&:hover': {
                  background: 'linear-gradient(45deg, #0077b5 30%, #006699 90%)',
                }
              }}
            >
              <TelegramIcon />
            </IconButton>
          </Tooltip>

          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 16,
              opacity: 0.7,
              fontSize: '0.7rem'
            }}
          >
            Made with 🦍 by{' '}
            <Link
              href="https://apeout.fun"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: 'inherit',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                }
              }}
            >
              Apeout
            </Link>
          </Typography>
        </Box>
      </div>
    </ThemeProvider>
  );
}

export default App;